import React, { useEffect } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { FloatingLabelInput } from '@/components/ui/floatinglable/floating';
import { LoadingButton } from '@/components/ui/common/loading-button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { RootState } from '@/redux/store';
import {
    useCreateSalesTaxMutation,
    useUpdateSalesTaxMutation,
} from '@/services/salesTaxApi';
import { SaleTax } from '@/types/system-setting/sale-tax';
import { Status } from '@/types/enums';
import { ErrorType, FormProps } from '@/types';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

interface SaleTaxFormData {
    id?: number;
    name: string;
    percentage: number;
    status: Status;
    is_srb: boolean;
    org_id: number;
}

const SaleTaxForm = ({
    isOpen,
    setOpen,
    data,
    setData,
    action,
    setAction,
    refetch,
}: FormProps<SaleTax>) => {
    const { toast } = useToast();
    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

    const [createSalesTax, { isLoading: createLoading }] = useCreateSalesTaxMutation();
    const [updateSalesTax, { isLoading: updateLoading }] = useUpdateSalesTaxMutation();

    const form = useForm<SaleTaxFormData>({
        mode: "all",
        defaultValues: {
            name: '',
            percentage: 1,
            status: Status.Active,
            is_srb: false,
            org_id: orgId,
        },
    });

    const {
        handleSubmit,
        reset,
        formState: { isSubmitting, errors },
    } = form;

    useEffect(() => {
        const initialValues = {
            name: '',
            percentage: 1,
            status: Status.Active,
            is_srb: false,
            org_id: orgId,
        };

        if (data && action === 'edit') {
            reset({
                id: data.id,
                name: data.name,
                percentage: data.percentage,
                status: data.status,
                is_srb: data.is_srb,
                org_id: orgId,
            });
        } else {
            // Always reset to initial values when switching to add mode
            reset(initialValues);
        }
    }, [data, action, reset, orgId]);


    const onSubmit = async (formData: SaleTaxFormData) => {
        const payload: SaleTax = {
            ...formData,
            name: formData.name.toLowerCase().trim(),
        };

        try {
            if (action === 'add') {
                const resp = await createSalesTax(payload);
                // Check for error first
                if (resp.error) {
                    const error = resp.error as FetchBaseQueryError & {
                        data?: { message?: string };
                    };
                    const errorMessage = ('status' in error && error.data && typeof error.data === 'object' && 'message' in error.data)
                        ? (error.data as { message?: string }).message || 'Something went wrong.'
                        : 'Something went wrong.';
                    toast({
                        variant: 'destructive',
                        title: 'Error in form Submission',
                        description: errorMessage,
                    });
                    return;
                }

                // Check for success
                if (resp.data) {
                    refetch();
                    toast({
                        variant: 'success',
                        title: 'Tax Group added successfully.',
                    });
                    handleClose();
                }
            } else {
                const resp = await updateSalesTax(payload);
                console.log({ resp });

                // Check for error first
                if (resp.error) {
                    const error = resp.error as FetchBaseQueryError & {
                        data?: { message?: string };
                    };
                    const errorMessage = ('status' in error && error.data && typeof error.data === 'object' && 'message' in error.data)
                        ? (error.data as { message?: string }).message || 'Something went wrong.'
                        : 'Something went wrong.';
                    toast({
                        variant: 'destructive',
                        title: 'Error in form Submission',
                        description: errorMessage,
                    });
                    return;
                }

                // Check for success
                if (resp.data) {
                    refetch();
                    toast({
                        variant: 'success',
                        title: 'Tax/VAT updated successfully.',
                    });
                    handleClose();
                }
            }
        } catch (error) {
            console.error('Error', { error });
            if (error && typeof error === 'object' && 'data' in error) {
                const typedError = error as ErrorType;
                toast({
                    variant: 'destructive',
                    title: 'Error in form Submission',
                    description: `${typedError.data?.detail ||
                        (typedError.data as { message?: string }).message
                        }`,
                });
            } else {
                toast({
                    variant: 'destructive',
                    title: 'Error in form Submission',
                    description: 'Something went wrong.',
                });
            }
        }
    };

    const handleClose = () => {
        // Force reset to initial values
        reset({
            name: '',
            percentage: 1,
            status: Status.Active,
            is_srb: false,
            org_id: orgId,
        }, {
            keepDefaultValues: false
        });
        setAction("add");
        setData(undefined);
        setOpen(false);
    };

    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent>
                <SheetHeader>
                    <SheetTitle>
                        {action === 'add' ? 'Create' : 'Edit'} Tax/VAT
                    </SheetTitle>
                    <SheetDescription>
                        <Separator className="h-[1px] font-thin rounded-full" />

                        <FormProvider {...form}>
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="flex flex-col py-4 gap-4"
                            >
                                <Controller
                                    name="name"
                                    control={form.control}
                                    rules={{
                                        required: 'Required',
                                        maxLength: {
                                            value: 40,
                                            message: 'Name must be 40 characters or less',
                                        },
                                        pattern: {
                                            value: /^[a-zA-Z]+[-'s]?[a-zA-Z ]+$/,
                                            message: 'Name should contain only alphabets',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <div className="space-y-1">
                                            <FloatingLabelInput
                                                id="name"
                                                label="Tax/VAT Name"
                                                text="*"
                                                className="capitalize"
                                                {...field}
                                            />
                                            {error && (
                                                <p className="text-xs text-red-500 mt-5 ">{error.message}</p>
                                            )}
                                        </div>
                                    )}
                                />

                                <Controller
                                    name="percentage"
                                    control={form.control}
                                    rules={{
                                        required: 'Required',
                                        min: {
                                            value: 0,
                                            message: 'Minimum percentage required is 0',
                                        },
                                        max: {
                                            value: 99,
                                            message: 'Maximum percentage required is 99',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <div className="space-y-1">
                                            <FloatingLabelInput
                                                id="percentage"
                                                type="number"
                                                min={0}
                                                max={99}
                                                step={0.1}
                                                label="Percentage"
                                                text="*"
                                                {...field}
                                                onChange={(e) => field.onChange(Number(e.target.value))}
                                            />
                                            {error && (
                                                <p className="text-xs text-red-500 mt-5 ">{error.message}</p>
                                            )}
                                        </div>
                                    )}
                                />

                                <Controller
                                    name="is_srb"
                                    control={form.control}
                                    render={({ field }) => (
                                        <div className="flex flex-col items-start space-y-2">
                                            <Label className="text-sm">
                                                {field.value ? "Disable" : "Enable"} SRB
                                            </Label>
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </div>
                                    )}
                                />

                                <Controller
                                    name="status"
                                    control={form.control}
                                    rules={{
                                        required: 'Required',
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <div className="space-y-1">
                                            <Select
                                                value={field.value || ''}
                                                onValueChange={(value) => {
                                                    if (value) {
                                                        field.onChange(value as Status);
                                                    } else {
                                                        field.onChange(undefined);
                                                    }
                                                }}
                                            >
                                                <SelectTrigger
                                                    floatingLabel="Status"
                                                    text="*"
                                                >
                                                    <SelectValue placeholder="Select Status">
                                                        <span className="flex gap-2 items-center">
                                                            <span
                                                                className={`w-2 h-2 rounded-full ${field.value === Status.Active
                                                                        ? "bg-green-500"
                                                                        : "bg-blue-500"
                                                                    }`}
                                                            ></span>
                                                            {field.value === Status.Active
                                                                ? "Active"
                                                                : "Inactive"}
                                                        </span>
                                                    </SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value={Status.Active}>
                                                        Active
                                                    </SelectItem>
                                                    <SelectItem value={Status.Inactive}>
                                                        Inactive
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                            {error && (
                                                <p className="text-xs text-red-500 mt-5 ">{error.message}</p>
                                            )}
                                        </div>
                                    )}
                                />

                                <LoadingButton
                                    type="submit"
                                    className="bg-primary font-semibold text-black gap-1"
                                    loading={isSubmitting || createLoading || updateLoading}
                                >
                                    {!isSubmitting && !createLoading && !updateLoading && (
                                        <i className="fa-regular fa-floppy-disk text-base px-1" />
                                    )}
                                    Save
                                </LoadingButton>
                            </form>
                        </FormProvider>
                    </SheetDescription>
                </SheetHeader>
            </SheetContent>
        </Sheet>
    );
};

export default SaleTaxForm;